import {map} from 'rxjs/operators';
import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {HttpClient, HttpHeaders, HttpParams} from '@angular/common/http';
import {ClassTransformer} from 'class-transformer';
import {AceLoggerService} from '@logging/ace-logger.service';
import {AceLogger} from '@logging/ace-logger';
import {BaseService} from '../api/base-service';
import {UserEntityType} from '@app/shared/services/api/user-entity';
import {User, UserImpl} from '@app/layout/admin/services/api/user-interfaces';
import {APIResponse} from '@app/layout/admin/services/api/api.interface';
import {HttpUtilityService} from '@shared/services/api/http-utility-service';
import {SiteId} from '../api/access-sites.interface';
import {PanelId, Submenu} from "@app/layout/components/sidebar/sidebar-item-factory.service";

interface AssociatedSite {
  user_id: string;
  site_id: number;
  site_name: string;
}

export enum ActionNotificationType {
  NO_NOTIFICATIONS = 'NO_NOTIFICATIONS',
  ASSIGNED = 'ASSIGNED',
  ALL_NOTIFICATIONS = 'ALL_NOTIFICATIONS'
}

export interface ActionNotificationPreferences extends Record<SiteId, ActionNotificationType | null> {}

export interface UserWithPreferences extends User {
  preferences: UserPreferences;
}

export interface CustomerEntityFeaturePreferences {
}

export interface CustomerEntityPreferences {
  features?: CustomerEntityFeaturePreferences;
}

export interface DashboardOptions {
  hide_site_map?: boolean;
}


export type LastSelectedNavigationMenus = Partial<Record<PanelId, Submenu['id']>>;


export interface UserPreferences {
  table_options: {rows_per_page: number};
  site_notification_options: ActionNotificationPreferences;
  notification_options: NotificationOptions;
  customer_preferences?: CustomerEntityPreferences;
  primary_selected_site_id?: number;
  dashboard_options?: DashboardOptions;
  last_selected_navigation_menus?: LastSelectedNavigationMenus;
}

export interface NonPersistedUserPreferences {
  tickets: {
    view: {
      activityContainerHeight: number;
    }
  }
}

export interface CustomerEntityPreferences {
  // if we have contractor/client entity feature prefs, use | to join them here.
  features?: CustomerEntityFeaturePreferences;
}

export interface NotificationOptions {
  mentions: boolean;
}

export interface UserEntityPreferences extends CustomerEntityPreferences {
  otp_required: boolean;
}

export interface UserAssociatedSite {
  site_id: number;
  site_name: string;
}

export class UserWithPreferencesImpl extends UserImpl implements UserWithPreferences {
  preferences: UserPreferences;
}

@Injectable({
  providedIn: 'root'
})
export class UserPreferenceService extends BaseService {

  private headers: HttpHeaders = this.getHeaders();
  private readonly PREFERENCES_OPERATION = 'preferences';
  private readonly DEFAULT_PREFERENCES: UserPreferences = {
    customer_preferences: {},
    table_options: {rows_per_page: 10},
    site_notification_options: {},
    notification_options: { mentions: true },
    dashboard_options: {hide_site_map: false}
  };
  private readonly logger: AceLogger;

  constructor(private http: HttpClient,
              private classTransformer: ClassTransformer,
              private httpUtilityService: HttpUtilityService,
              logService: AceLoggerService) {
    super();
    this.logger = logService.create(UserPreferenceService.name);
  }

  getUserPreferences(userId: string): Observable<UserPreferences> {
    const options = {headers: this.headers};
    return this.http.get(this.getGateway() + '/users/users/' + userId + '/preferences', options)
      .pipe(map((response: APIResponse<UserPreferences>) => this.httpUtilityService.extractResponseData(response, this.PREFERENCES_OPERATION)));
  }

  updateUserPreferences(userId: string, userPreferences: UserPreferences): Observable<Object> {
    const { customer_preferences, ...preferencesToUpdate } = userPreferences;
    const options = {headers: this.headers};
    return this.http.put(this.getGateway() + '/users/users/' + userId + '/preferences', preferencesToUpdate, options);
  }

  patchUserPreferences(userId: string, userPreferences: Partial<UserPreferences>): Observable<void> {
    const options = {headers: this.headers};
    return this.http.patch<void>(this.getGateway() + '/users/users/' + userId + '/preferences', userPreferences, options);
  }

  public mergeWithDefaultPreferences(prefs: UserPreferences): UserPreferences {
    if (!prefs) {
      prefs = <UserPreferences>{};
    }

    prefs.table_options = {
      ...this.DEFAULT_PREFERENCES.table_options,
      ...prefs.table_options
    };

    prefs.notification_options = {
      ...this.DEFAULT_PREFERENCES.notification_options,
      ...prefs.notification_options
    };

    return prefs;
  }

  private getUserEntityResource(userEntityType: UserEntityType): string {
    const entityResource = userEntityType === UserEntityType.CUSTOMER ? 'customers'
      : userEntityType === UserEntityType.CLIENT ? 'clients'
      : userEntityType === UserEntityType.CONTRACTOR ? 'contractors'
      : null;
    if (!entityResource) {
      throw new Error('Invalid UserEntityType ' + userEntityType);
    }
    return entityResource;
  }

  getUserEntityPreferences(userEntityId: number, userEntityType: UserEntityType): Observable<UserEntityPreferences> {
    const options = {headers: this.headers};
    return this.http.get(this.getGateway() + '/users/' + this.getUserEntityResource(userEntityType) + '/' + userEntityId + '/preferences', options)
      .pipe(map(response => this.httpUtilityService.extractResponseDataCompat(response, 'preferences')));
  }

  updateUserEntityPreferences(userEntityId: number, userEntityType: UserEntityType, userEntityPreferences: UserEntityPreferences): Observable<void> {
    const options = {headers: this.headers};
    return this.http.put<void>(this.getGateway() + '/users/' + this.getUserEntityResource(userEntityType) + '/' + userEntityId + '/preferences', userEntityPreferences, options);
  }

  getAssociatedSites(userIds: string[]): Observable<Map<string, UserAssociatedSite[]>> {
    let params = new HttpParams();
    userIds.forEach(userId => {
      params = params.append('user_id', userId);
    });
    const options = {headers: this.headers, params: params};
    return this.http.get(this.getGateway() + '/sites/associated-sites', options)
      .pipe(map((response) => {
        const siteMap = new Map<string, UserAssociatedSite[]>();
        response[this.DATA_PREFIX]['associated_sites'].forEach((site: AssociatedSite) => {
          const sites = siteMap.get(site.user_id);
          if (sites == null) {
            siteMap.set(site.user_id, [{site_id: site.site_id, site_name: site.site_name}]);
          } else {
            sites.push({site_id: site.site_id, site_name: site.site_name});
          }
        });
        return siteMap;
      }));
  }

  getSiteNotificationPreferences(siteId: number, userIds: string[]): Observable<UserWithPreferences[]> {
    let params = new HttpParams();
    userIds.forEach(userId => {
      params = params.append('user_id', userId);
    });
    params = params.append('notification_preferences_site_id', siteId.toString());
    params = params.append('include_notification_preferences', 'true');
    const options = {headers: this.headers, params: params};
    return this.http.get(this.getGateway() + '/users/users', options).pipe(map((response) => {
      return this.classTransformer.plainToInstance(UserWithPreferencesImpl, response[this.DATA_PREFIX]['users']);
    }));
  }
}
